import { gql, Resolver } from '@apollo/client';

import { taskUploadToS3 } from './taskUploadToS3';
import { taskResizeImage } from './taskResizeImage';

interface Args {
    bucketName: string;
    bucketRegion: string;
    file: File;
    identityPoolId: string;
    key: string;
    maxSize: number;
    taskId: string;
}

interface Context {
    sendTaskEvent: SendTaskEvent;
    bindManagedUpload: BindManagedUpload;
}

interface Result {
    success: boolean;
    canceled: boolean;
}

/**
 * Resize an image and then upload it to S3.
 */
export const taskResizeAndUploadImage: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    context: Context,
): Promise<Result> => {
    const {
        bucketName,
        bucketRegion,
        file,
        identityPoolId,
        key,
        maxSize,
        taskId,
    } = args;

    // 1. Resize image
    const { blob } = await taskResizeImage(
        obj,
        {
            file,
            maxSize,
            doTaskUpdates: {
                error: true,
                success: false,
                progress: true,
            },
            taskId,
            progressOffset: 0,
            progressShare: 5,
        },
        context,
    );

    let uploadFile = file;

    if (blob !== null) {
        uploadFile = new File([blob], file.name);
    }

    // 2. Upload image to S3
    const uploadResult = await taskUploadToS3(
        obj,
        {
            bucketName,
            bucketRegion,
            file: uploadFile,
            doTaskUpdates: {
                error: true,
                progress: true,
                success: true,
            },
            identityPoolId,
            key,
            taskId,
            progressOffset: 5,
            progressShare: 95,
        },
        context,
    );

    if (uploadResult.aborted || !uploadResult.completed) {
        return {
            success: false,
            canceled: true,
        };
    }

    return {
        success: true,
        canceled: false,
    };
};

export const TASK_RESIZE_AND_UPLOAD_IMAGE = gql`
    mutation TaskResizeAndUploadImage(
        $bucketName: String!
        $bucketRegion: String!
        $file: File!
        $identityPoolId: String!
        $key: String!
        $maxSize: Int!
        $taskId: ID!
    ) {
        taskResizeAndUploadImage(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            file: $file
            identityPoolId: $identityPoolId
            key: $key
            maxSize: $maxSize
            taskId: $taskId
        ) @client
    }
`;
